<script setup lang="ts">
import type { SectionNode } from "@/domains/v4b/site-section/site-section.types";
import type {
  ISelectSectionSimpleTreeExpose,
  ISelectSectionSimpleTreeNodeTag,
  IVirtualizedTreeNode,
} from "@voltalis/ui-kit/components";

import { SearchableSelectSectionSimpleTree } from "@voltalis/ui-kit/components";
import { computed, ref, useTemplateRef, watch } from "vue";

import { buildSelectSectionParentDialogNodeTags } from "@/app/tools/new-section-management/site-section-creation/select-section-parent-dialog-tags";
import {
  applyPendingSiteSectionPreview,
  isPendingSiteSectionPreviewNode,
} from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree-pending-preview";
import { scrollSiteSectionPreviewIntoView } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree-pending-preview-scroll";
import {
  sectionHasAppliances,
  TSiteSectionsTreeData,
} from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { TranslationKey, useTypedIntl } from "@/locales/typed-intl";

import Dialog from "primevue/dialog";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

interface IProps {
  siteSectionsTree?: IVirtualizedTreeNode<TSiteSectionsTreeData>;
  initialSelectedParent: TSiteSectionsTreeData;
  maybeInitialSectionParent?: TSiteSectionsTreeData;
  targetSiteRootTranslationKey?: TranslationKey;
  isNodeDisabled?: (node: IVirtualizedTreeNode<TSiteSectionsTreeData>) => boolean;
  pendingPreviewName?: string;
  pendingPreviewSection?: SectionNode;
}

interface IEmits {
  (e: "select", parentSectionId: TSiteSectionsTreeData): void;
}

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();

const visible = defineModel<boolean>({
  required: true,
});

const { t, k } = useTypedIntl();

const selectedParentSection = ref<TSiteSectionsTreeData>(null);
const selectSectionTree = useTemplateRef<ISelectSectionSimpleTreeExpose<TSiteSectionsTreeData>>("selectSectionTree");

const chooseButtonLabel = computed<string>(() => {
  const sectionName = selectedParentSection.value?.name ?? "";
  return t(k.tools.siteManagement.selectParentSection.choose({ sectionName }));
});

const siteSectionsTreeWithPendingPreview = computed<IVirtualizedTreeNode<TSiteSectionsTreeData> | undefined>(() => {
  if (!props.pendingPreviewName) return props.siteSectionsTree;

  if (props.pendingPreviewSection) {
    return applyPendingSiteSectionPreview(props.siteSectionsTree, {
      type: "move",
      name: props.pendingPreviewName,
      section: props.pendingPreviewSection,
      parent: selectedParentSection.value,
      sourceTree: props.siteSectionsTree,
    });
  }

  return applyPendingSiteSectionPreview(props.siteSectionsTree, {
    type: "creation",
    name: props.pendingPreviewName,
    parent: selectedParentSection.value,
    sourceTree: props.siteSectionsTree,
  });
});

function handleSelect(selection: TSiteSectionsTreeData) {
  selectedParentSection.value = selection;
}

function showRadioButtonCb(node: IVirtualizedTreeNode<TSiteSectionsTreeData>): boolean {
  if (isPendingSiteSectionPreviewNode(node)) return false;

  return !sectionHasAppliances(node.data);
}

function isNodeDisabled(node: IVirtualizedTreeNode<TSiteSectionsTreeData>): boolean {
  return isPendingSiteSectionPreviewNode(node) || (props.isNodeDisabled?.(node) ?? false);
}

function getNodeTagsCb(node: IVirtualizedTreeNode<TSiteSectionsTreeData>): ISelectSectionSimpleTreeNodeTag[] {
  return buildSelectSectionParentDialogNodeTags({
    node,
    maybeInitialSectionParent: props.maybeInitialSectionParent,
    labels: {
      parent: t(k.tools.siteManagement.selectParentSection.parent),
    },
  });
}

watch(
  () => props.initialSelectedParent,
  (newValue) => {
    selectedParentSection.value = newValue;
  },
  { immediate: true },
);

watch([visible, siteSectionsTreeWithPendingPreview], async () => {
  if (!visible.value || !props.pendingPreviewName) return;

  await scrollSiteSectionPreviewIntoView(selectSectionTree);
});
</script>

<template>
  <Dialog
    v-model:visible="visible"
    class="select-section-parent-dialog bg-white w-[856px]"
    modal
    :data-test-id="DATA_TEST_IDS.components.selectSectionParentDialog.component"
  >
    <template #container="{ closeCallback }">
      <div class="p-dialog-custom-container">
        <div class="p-dialog-custom-container__header flex justify-between gap-4 border-b">
          <div class="flex flex-col gap-1">
            <h3 class="text-lg font-semibold">{{ t(k.tools.siteManagement.selectParentSection.title) }}</h3>
            <span class="text-base text-gray-500">{{ t(k.tools.siteManagement.selectParentSection.subtitle) }}</span>
          </div>

          <HospitalityButton
            :data-test-id="DATA_TEST_IDS.components.selectSectionParentDialog.closeButton"
            severity="secondary"
            @click="closeCallback"
          >
            <i class="pi pi-arrow-left pr-2" />
            {{ t(k.global.misc.back) }}
          </HospitalityButton>
        </div>

        <div class="p-dialog-custom-container__content flex flex-col gap-[10px] h-[466px]">
          <small class="uppercase text-gray-500">{{ t(k.tools.siteManagement.selectParentSection.destination) }}</small>

          <SearchableSelectSectionSimpleTree
            ref="selectSectionTree"
            :get-node-tags-cb="getNodeTagsCb"
            :initialSelectedSection="props.initialSelectedParent"
            :is-node-disabled="isNodeDisabled"
            :siteSectionsTree="siteSectionsTreeWithPendingPreview"
            :showRadioButtonCb="showRadioButtonCb"
            @selection="handleSelect($event)"
          />
        </div>

        <div class="p-dialog-custom-container__footer flex w-full justify-between items-center border-t gap-3">
          <div
            v-if="selectedParentSection"
            class="flex items-center gap-1"
          >
            <span class="text-gray-500">{{ t(k.tools.siteManagement.selectParentSection.to) }}</span>
            <span
              class="font-semibold text-gray-500 overflow-hidden whitespace-nowrap text-ellipsis"
              :data-test-id="DATA_TEST_IDS.components.selectSectionParentDialog.targetSectionName"
            >
              {{ selectedParentSection.name }}
            </span>
          </div>
          <span
            v-else
            class="text-gray-500"
            :data-test-id="DATA_TEST_IDS.components.selectSectionParentDialog.targetSiteRoot"
          >
            <template v-if="props.targetSiteRootTranslationKey">
              {{ t(props.targetSiteRootTranslationKey) }}
            </template>
            <template v-else>{{ t(k.tools.siteManagement.selectParentSection.targetRoot) }}</template>
          </span>

          <HospitalityButton
            :data-test-id="DATA_TEST_IDS.components.selectSectionParentDialog.chooseButton"
            :label="chooseButtonLabel"
            @click="emits('select', selectedParentSection)"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<style lang="scss">
.select-section-parent-dialog {
  .virtualized-tree-item-container:has(.select-section-tree-badge--destination) {
    @apply px-2;

    > [data-test-id="select-section-simple-tree/section-item"] {
      @apply rounded-lg border border-dashed border-brand bg-white;
    }
  }

  [data-test-id="select-section-simple-tree/section-item"]:has(.select-section-tree-badge--destination) {
    @apply opacity-100;

    [data-test-id="select-section-simple-tree/section-name"] > span:first-child {
      @apply text-gray-400;
    }
  }

  .select-section-tree-badge--destination {
    @apply hidden;
  }
}
</style>
